import { get, isEqual } from "lodash-es";
import { EditorState, TextSelection, type Transaction } from "prosemirror-state";
import { AddMarkStep, Transform } from "prosemirror-transform";
import { EditorView } from "prosemirror-view";
import Delta from "quill-delta";
import type { Doc, ObjectReplaceOp, Op } from "sharedb/lib/client";

import type {
  PresenceDataType,
  PresenceViewShortType,
  RichTextOpType,
} from "../../../../../shared/types/sharedb";
import type {
  PathType,
  RichTextCellType,
  WorksheetType,
} from "../../../../../shared/types/worksheet";
import { maybeAddInitializationWithOp } from "../../../../../shared/worksheets/doc-helpers";
import { getUserColor } from "../../../../helpers/colors";

import {
  convertDeltaToProseMirrorDoc,
  convertDeltaToProseMirrorSteps,
  convertProseMirrorStepsToDelta,
  convertToDeltaPosition,
  convertToProseMirrorPosition,
  schema,
} from "./adapter";
import { isLinkMark } from "./adapter/schema";
import { formulaNodeView } from "./node-views/formula";
import { imageNodeView } from "./node-views/image";
import { key as characterCountKey, characterCountPlugin } from "./plugins/character-count";
import { handleImageUploads, imageDropPlugin } from "./plugins/image-drop";
import { linkPopoverPlugin } from "./plugins/link-popover";
import { key as pageStateKey, pageStatePlugin } from "./plugins/page-state";
import { placeholderPlugin } from "./plugins/placeholder";
import { UPDATE_REMOTE_SELECTIONS_META, remoteSelectionsPlugin } from "./plugins/selections";
import { createBasePlugins } from "./plugins/setup";
import { getActivePanelId, toolbarPluginKey } from "./plugins/toolbar";
import type { AuthoredTransaction, RemoteSelectionData } from "./types";
import {
  addLinkMark,
  extractUrlFromPastedContent,
  isFormulaSelected,
  isImageSelected,
  isSelectionInCodeBlock,
  partitionOps,
  remapSelections,
  transformLinkUrl,
  validateLinkUrl,
} from "./utils";

import { isProjectorView } from "components/materials/presentation/projector/projector-messaging";
import { UserType } from "components/server-types";
import { t } from "i18n/i18n";
import { logInfoWithDebugFlag, rollbarAndLogError } from "utils/logger";
import { getUserShortName } from "utils/user-utils";

const PRESENCE_RENDER_DELAY_MS = 300; // Delay before rendering remote selections to handle presence/op race condition
const KEYBOARD_ANIMATION_DELAY_MS = 300; // iOS soft-keyboard slide-up duration.
const KEYBOARD_CURSOR_MARGIN_PX = 40; // Padding between the cursor and the top of the soft keyboard.

export interface ProseMirrorEditorInstanceOptions {
  doc: Doc<WorksheetType>;
  path: PathType;
  fieldPath?: PathType;
  readOnly: boolean;
  userId?: string | number;
  canAuthorSection: boolean;
  presenceViewType?: PresenceViewShortType;
  users: Record<string, UserType>;
  embedded?: boolean;
  create?: boolean;
  pageId?: string;
  isAuthoringPage?: boolean;
  showPlaceholder?: boolean;
  placeHolderText?: string;
  characterCounter: "NONE" | "READ" | "EDIT";
  maxLength?: number | null;
  onEmbedURLFromPaste?: (url: string) => void;
  onEmbedURLFromLink?: (url: string) => void;
}

/**
 * Manages a ProseMirror EditorView lifecycle: initialization, ShareDB integration, and cleanup.
 *
 * A plain class (rather than a React component) gives stable references and explicit lifecycle
 * control, since ProseMirror is an imperative library that manages its own DOM.
 */
export class ProseMirrorEditorInstance {
  private editorView: EditorView | null = null;

  private rootElement: HTMLElement;

  private sharedbDoc: Doc<WorksheetType>;

  private path: PathType;

  private fieldPath: PathType | undefined;

  private readOnly: boolean;

  private editorState: EditorState;

  private userId?: string | number;

  private canAuthorSection: boolean;

  private presenceViewType?: PresenceViewShortType;

  private users: Record<string, UserType>;

  private create: boolean;

  private embedded: boolean;

  private pageId?: string;

  private onEmbedURLFromPaste?: (url: string) => void;

  private onEmbedURLFromLink?: (url: string) => void;

  private opHandler: ((ops: Op[], source: unknown) => void) | null = null;

  private presenceHandler: ((srcPresenceId: string, presence: PresenceDataType) => void) | null =
    null;

  // Maps ShareDB presence IDs to user IDs for tracking remote cursors
  private presenceIdMapping: Record<string, string> = {};

  // Stores remote selection data keyed by srcPresenceId
  private remoteSelectionsData: Map<string, RemoteSelectionData> = new Map();

  // Tracks active timeouts for rendering selections, keyed by userId
  // Stores timeout and updateCallback so we can execute updateCallback immediately when op arrives
  private remoteSelectionsTimeouts: Map<
    string,
    { timeout: ReturnType<typeof setTimeout>; updateCallback: () => void }
  > = new Map();

  /**
   * Workaround to filter out known erroneous or corrupt ops client-side.
   * Ops that are missing "insert" or have "retain" break the Delta/ProseMirror adapter. We filter
   * them out for display and, when editable, submit delete ops to fix the doc in ShareDB.
   */
  private removeInvalidOps(originalOps: RichTextCellType["ops"] = []): RichTextCellType["ops"] {
    const { validOps, invalidOps } = partitionOps(originalOps);

    if (invalidOps.length === 0) return originalOps;

    console.warn("[ProseMirrorEditor] Filtering non-insert ops client-side", {
      pageId: this.pageId,
      path: this.path,
      originalOps: JSON.stringify(originalOps),
      validOps: JSON.stringify(validOps),
      invalidOps: JSON.stringify(invalidOps),
    });

    if (!this.readOnly) {
      // Submit operations to delete the invalid ops
      // Delete in reverse order (highest index first) to prevent index shifting issues
      // when multiple deletions happen sequentially
      const removeOps = [...invalidOps]
        .sort((a, b) => b.index - a.index)
        .map(({ index, op }) => ({
          p: [...this.path, "ops", index],
          ld: op,
          ldCheck: true as const, // custom flag to enable safety check to prevent deleting wrong element
        }));
      this.sharedbDoc.submitOp(removeOps, { source: this }, (err) => {
        if (err) {
          rollbarAndLogError(
            "[ProseMirrorEditor] Error submitting corrective ops",
            { pageId: this.pageId, path: this.path },
            err,
          );
        }
      });
    }

    return validOps.map(({ op }) => op);
  }

  constructor(rootElement: HTMLElement, options: ProseMirrorEditorInstanceOptions) {
    this.rootElement = rootElement;
    this.sharedbDoc = options.doc;
    this.path = options.path;
    this.fieldPath = options.fieldPath;
    this.readOnly = options.readOnly;
    this.userId = options.userId;
    this.presenceViewType = options.presenceViewType;
    this.users = options.users;
    this.canAuthorSection = options.canAuthorSection;
    this.create = options.create ?? false;
    this.embedded = options.embedded ?? false;
    this.pageId = options.pageId;
    this.onEmbedURLFromPaste = options.onEmbedURLFromPaste;
    this.onEmbedURLFromLink = options.onEmbedURLFromLink;

    // Load initial content from ShareDB doc
    const initialData = get(this.sharedbDoc.data, this.path) as RichTextCellType;
    const ops = initialData?.ops ?? undefined;
    const showToolbar = !this.readOnly && !this.embedded;
    const plugins = [
      ...createBasePlugins(showToolbar),
      pageStatePlugin({ isAuthoring: options.isAuthoringPage ?? false, rootId: rootElement.id }),
      placeholderPlugin({
        showPlaceholder: options.showPlaceholder ?? false,
        placeHolderText: options.placeHolderText?.trim() || t("worksheet.placeholder.content"),
        readOnly: this.readOnly,
      }),
      remoteSelectionsPlugin(),
      linkPopoverPlugin(),
      characterCountPlugin({
        limit: options.maxLength,
        display: options.characterCounter,
        enforce: true,
      }),
    ];
    if (!this.readOnly) {
      plugins.push(imageDropPlugin());
    }
    const emptyState = EditorState.create({ schema, plugins });
    let initialState = emptyState;

    if (ops && ops.length > 0) {
      const validOps = this.removeInvalidOps(ops);
      if (validOps.length > 0) {
        try {
          const proseMirrorDoc = convertDeltaToProseMirrorDoc(new Delta(validOps));
          initialState = EditorState.create({
            schema,
            doc: proseMirrorDoc,
            plugins,
          });
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          rollbarAndLogError(
            "[ProseMirrorEditor] Error initializing editor",
            { pageId: this.pageId, path: this.path, ops: validOps },
            error,
          );
        }
      }
    }

    this.editorState = initialState;

    // This attaches directly to the DOM element and manages its own DOM
    this.editorView = new EditorView(this.rootElement, {
      state: initialState,
      editable: () => !this.readOnly,
      nodeViews: {
        formula: formulaNodeView,
        image: imageNodeView,
      },
      dispatchTransaction: (tr) => {
        this.handleTransaction(tr);
      },
      handleDOMEvents: {
        blur: () => {
          this.sharedbDoc.localPresence.submit(null);
          return false; // let ProseMirror handle blur normally
        },
      },
      attributes: {
        // used for Tailwind CSS:
        // https://v3.tailwindcss.com/docs/hover-focus-and-other-states#differentiating-nested-groups
        class: "group/prosemirror",
      },
      handlePaste: (view, event, slice) => {
        if (this.readOnly) return true; // should never happen

        // Check if selection should be turned into a link mark
        if (
          !view.state.selection.empty &&
          !isSelectionInCodeBlock(view.state) &&
          !isFormulaSelected(view.state) &&
          !isImageSelected(view.state)
        ) {
          const pastedText = event.clipboardData?.getData("text");
          const href = pastedText?.includes(".") ? transformLinkUrl(pastedText) : undefined;
          if (href && validateLinkUrl(href) === null) {
            const { from, to } = view.state.selection;
            view.dispatch(addLinkMark(view.state.tr, from, to, href).scrollIntoView());
            return true; // paste handled; skip ProseMirror's default paste behavior
          }
        }

        // Check if the pasted text is a URL and offer to embed it
        if (this.onEmbedURLFromPaste) {
          const firstURL = extractUrlFromPastedContent(slice, event);
          if (firstURL) {
            this.onEmbedURLFromPaste(firstURL);
            return false; // let ProseMirror handle paste normally
          }
        }

        // Check if the pasted text is a file and upload it
        const data = event.clipboardData;
        if (data && data.types.includes("Files")) {
          const files = [...data.items].flatMap((item) => {
            if (item.kind !== "file") return [];
            const file = item.getAsFile();
            return file ? [file] : [];
          });
          if (files.length > 0) {
            handleImageUploads(view, ...files);
            return true;
          }
        }

        return false;
      },
    });

    this.editorView.dom.setAttribute("data-testid", "rich-text-editor");

    this.setupShareDB();
  }

  /**
   * Closes the active toolbar panel if one is open, restores editor focus, and resets
   * the toolbar's roving tabindex. Returns true if a panel was closed.
   */
  dismissToolbarPanel(): boolean {
    if (!this.editorView) return false;

    if (getActivePanelId(this.editorView.state) === "main") return false;

    this.editorView.focus(); // keep :focus-within alive during dispatch
    this.editorView.dispatch(
      this.editorView.state.tr.setMeta(toolbarPluginKey, {
        activePanelId: "main",
        shouldFocus: true,
      }),
    );
    return true;
  }

  /** Resets the toolbar to the default panel. Used when focus leaves the editor context. */
  resetToolbarPanel(): void {
    if (!this.editorView) return;
    this.editorView.dispatch(
      this.editorView.state.tr.setMeta(toolbarPluginKey, { activePanelId: "main" }),
    );
  }

  /** Resets the toolbar scroll position. Used when focus returns to the editor. */
  resetToolbarScroll(): void {
    this.editorView?.dispatch(
      this.editorView.state.tr.setMeta(toolbarPluginKey, { resetScroll: true }),
    );
  }

  /** Destroy the editor instance and clean up. */
  destroy(): void {
    // Remove ShareDB listeners
    if (this.opHandler) {
      this.sharedbDoc.removeListener("op", this.opHandler);
      this.opHandler = null;
    }

    // Remove presence listeners
    if (this.presenceHandler) {
      this.sharedbDoc.presence.off("receive", this.presenceHandler);
      this.presenceHandler = null;
    }

    // Clear all pending remote selections timeouts
    for (const { timeout } of this.remoteSelectionsTimeouts.values()) {
      clearTimeout(timeout);
    }
    this.remoteSelectionsTimeouts.clear();

    // Destroy the editor view
    if (this.editorView) {
      this.editorView.destroy();
      this.editorView = null;
    }
  }

  /** Sync editor contents from external doc.data changes (e.g., Timeline view, ShareDB error recovery, blocks moved). */
  syncContentsFromDoc(data: Delta, newPath: PathType): void {
    if (!this.editorView) return;

    this.path = newPath;

    const ops = data?.ops ?? undefined;
    const emptyDoc = schema.topNodeType.createAndFill()!;
    let tr = this.editorState.tr.replaceWith(0, this.editorState.doc.content.size, emptyDoc);

    if (ops && ops.length > 0) {
      const validOps = this.removeInvalidOps(ops);
      if (validOps.length > 0) {
        try {
          const proseMirrorDoc = convertDeltaToProseMirrorDoc(new Delta(validOps));
          tr = this.editorState.tr.replaceWith(
            0,
            this.editorState.doc.content.size,
            proseMirrorDoc,
          );
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          rollbarAndLogError(
            "[ProseMirrorEditor] Error syncing editor contents from ShareDB doc",
            { pageId: this.pageId, path: this.path, ops: validOps },
            error,
          );
          return;
        }
      }
    }

    // Update state and view directly (bypassing handleTransaction to avoid ShareDB submission)
    tr.setMeta("addToHistory", false);
    const { state: newState } = this.editorState.applyTransaction(tr);
    this.editorState = newState;
    this.editorView.updateState(this.editorState);

    // Clear stale remote cursor positions — old positions are invalid in the new doc.
    // Cursors self-restore on the next handlePresence event from each active user.
    this.remoteSelectionsData.clear();
    this.renderRemoteSelections();
  }

  /**
   * Focus the editor and place the cursor at the given viewport coordinates.
   */
  focusAtCoords(clientX: number, clientY: number): void {
    if (!this.editorView) return;
    const result = this.editorView.posAtCoords({ left: clientX, top: clientY });
    if (result) {
      const $pos = this.editorView.state.doc.resolve(result.pos);
      const tr = this.editorView.state.tr.setSelection(TextSelection.near($pos));
      this.editorView.dispatch(tr);
    }
    this.editorView.focus();
  }

  /**
   * Scroll the cursor into view above the soft keyboard. iOS does not reliably
   * auto-scroll for programmatic focus, so we wait for the keyboard slide-up
   * animation and then ask the browser to scroll the cursor's element into view.
   * `block: "center"` keeps a comfortable gap above the keyboard without us
   * having to math out the visual viewport ourselves.
   */
  scrollCursorAboveKeyboard(): void {
    setTimeout(() => {
      const view = this.editorView;
      if (!view) return;
      const cursor = view.coordsAtPos(view.state.selection.head);
      const visibleBottom =
        (window.visualViewport?.height ?? window.innerHeight) - KEYBOARD_CURSOR_MARGIN_PX;
      if (!cursor || cursor.bottom <= visibleBottom) return;
      const { node } = view.domAtPos(view.state.selection.head);
      const element = node instanceof Element ? node : node.parentElement;
      element?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, KEYBOARD_ANIMATION_DELAY_MS);
  }

  setIsAuthoringPage(enabled: boolean): void {
    if (!this.editorView) return;
    this.editorView.dispatch(
      this.editorView.state.tr.setMeta(pageStateKey, { isAuthoring: enabled }),
    );
  }

  setFieldPath(fieldPath: PathType | undefined): void {
    this.fieldPath = fieldPath;
  }

  updateCharacterCountLimit(limit: number | null): void {
    if (!this.editorView) return;
    this.editorView.dispatch(this.editorView.state.tr.setMeta(characterCountKey, { limit }));
  }

  updateCharacterCountMode(mode: "NONE" | "READ" | "EDIT"): void {
    if (!this.editorView) return;
    this.editorView.dispatch(
      this.editorView.state.tr.setMeta(characterCountKey, { display: mode }),
    );
  }

  updateOnEmbedURLFromPaste(callback: ((url: string) => void) | undefined): void {
    this.onEmbedURLFromPaste = callback;
  }

  updateOnEmbedURLFromLink(callback: ((url: string) => void) | undefined): void {
    this.onEmbedURLFromLink = callback;
  }

  private remapRemoteSelections(authoredTransactions: AuthoredTransaction[]): void {
    const updated = remapSelections(this.remoteSelectionsData, authoredTransactions);
    if (updated) {
      this.remoteSelectionsData = updated;
      this.renderRemoteSelections();
    }
  }

  /** Applies a transaction, updates the view, and submits doc changes to ShareDB. */
  private handleTransaction(transaction: Transaction): void {
    if (!this.editorView) return;

    const prevDoc = this.editorState.doc;
    const { state: newState, transactions } = this.editorState.applyTransaction(transaction);
    this.editorState = newState;
    this.editorView.updateState(this.editorState);

    // In readOnly mode, skip ShareDB operations but still apply transactions locally
    if (this.readOnly) return;

    const docChangedTrs = transactions.filter((tr) => tr.docChanged);
    if (newState.doc !== prevDoc || docChangedTrs.length) {
      // Keep remote cursors stable while local doc changes settle
      this.remapRemoteSelections(docChangedTrs.map((tr) => ({ transaction: tr })));

      // Submit document changes to ShareDB
      try {
        let delta = convertProseMirrorStepsToDelta(docChangedTrs[0]);
        for (let i = 1; i < docChangedTrs.length; i += 1) {
          delta = delta.compose(convertProseMirrorStepsToDelta(docChangedTrs[i]));
        }
        if (delta.ops.length === 0) return;

        const ops: (RichTextOpType | Op)[] = [
          {
            p: this.path,
            t: "rich-text",
            o: delta.ops,
            u: String(this.userId ?? ""),
          },
        ];

        // If create is true and the field doesn't exist, initialize it first
        if (this.create) {
          // This code ensures that pointing a ProseMirrorRichTextField to an empty data structure initializes
          // the structure before adding content data to it. Right now, this is only used for cells
          // in TableFields.
          // Path is either e.g. ['d', 'g', '<fieldId>', 'cm', '<rowId, colId>', 'v']
          // or             e.g. ['b', '0', 'f', '1', 'v', 'cm', '<rowId, colId>', 'v']
          // Note that this assumes that `cm` is initialized, and will overwrite any existing data
          // in cm.<rowId, colId> if data exists there (e.g. `ro` is set), but "v" doesn't.
          const currentData = get(this.sharedbDoc.data, this.path) as RichTextCellType;
          const pathToParent = this.path.slice(0, -1);

          if (!currentData) {
            // need to create the field, before we can modify it
            const lastPathPart = this.path[this.path.length - 1];
            ops.unshift({
              p: pathToParent,
              oi: { [lastPathPart]: {} },
            });
          }
        }

        const newOps = maybeAddInitializationWithOp(this.sharedbDoc, ops, this.path);

        logInfoWithDebugFlag("debug_prosemirror", "Submitting ops:", {
          pageId: this.pageId,
          path: this.path,
          ops: newOps,
        });

        // Include source to prevent feedback loops
        this.sharedbDoc.submitOp(newOps, { source: this }, (err) => {
          if (err) {
            rollbarAndLogError(
              "[ProseMirrorEditor] Error submitting ops to ShareDB",
              { pageId: this.pageId, path: this.path },
              err,
            );
          }
        });
      } catch (e) {
        const error = e instanceof Error ? e : new Error(String(e));
        rollbarAndLogError(
          "[ProseMirrorEditor] Error processing local transaction",
          {
            pageId: this.pageId,
            path: this.path,
            docChanged: docChangedTrs.length > 0,
            steps: docChangedTrs.flatMap((tr) => tr.steps.map((s) => s.toJSON())),
            stepCount: docChangedTrs.reduce((n, tr) => n + tr.steps.length, 0),
          },
          error,
        );
      }

      // Detect link mark additions and offer to embed the URL
      if (this.onEmbedURLFromLink && docChangedTrs.length > 0) {
        for (const tr of docChangedTrs) {
          for (const step of tr.steps) {
            if (step instanceof AddMarkStep && isLinkMark(step.mark)) {
              const { href } = step.mark.attrs;
              if (href) this.onEmbedURLFromLink(href);
            }
          }
        }
      }
    } else if (this.canAuthorSection) {
      // Doc did not change, but metadata might have.
      // Only instructors can change metadata, so we only attempt to sync to ShareDB
      // if the user can author the section.
      const characterCountMeta = transaction.getMeta(characterCountKey);
      if (characterCountMeta && typeof characterCountMeta.limit !== "undefined" && this.fieldPath) {
        const maxLengthPath = [...this.fieldPath, "maxLength"] as PathType;
        // Missing key (undefined) and explicit null both mean "no limit". Do not
        // write just to normalize, or viewing a page bumps the worksheet version.
        const rawPrevMaxLength = get(this.sharedbDoc.data, maxLengthPath) as
          | number
          | null
          | undefined;
        const prevMaxLength = rawPrevMaxLength ?? null;
        const nextMaxLength = (characterCountMeta.limit ?? null) as number | null;
        if (prevMaxLength !== nextMaxLength) {
          const op = {
            p: maxLengthPath,
            oi: nextMaxLength,
            od: rawPrevMaxLength,
          } as ObjectReplaceOp;

          logInfoWithDebugFlag("debug_prosemirror", "Submitting op:", {
            pageId: this.pageId,
            path: this.path,
            op,
          });
          // Include source to prevent feedback loops
          this.sharedbDoc.submitOp(op, { source: this }, (err) => {
            if (err) {
              rollbarAndLogError("[ProseMirrorEditor] Error submitting ops to ShareDB", {
                pageId: this.pageId,
                path: this.path,
                err,
              });
            }
          });
        }
      }
    }

    // Submit presence after op is queued to ensure position is correct relative to updated document
    if (transactions.some((tr) => tr.selectionSet)) this.submitPresenceUpdate();
  }

  /** Set up ShareDB event listeners. */
  private setupShareDB(): void {
    const _handleOp = (
      ops: RichTextOpType[],
      source: InstanceType<typeof ProseMirrorEditorInstance>,
    ) => {
      this.handleOp(ops, source);
    };

    const handlePresence = (srcPresenceId: string, presence: PresenceDataType) => {
      this.handlePresence(srcPresenceId, presence);
    };

    this.sharedbDoc.on("op", _handleOp);
    this.sharedbDoc.presence.on("receive", handlePresence);

    this.opHandler = _handleOp;
    this.presenceHandler = handlePresence;
  }

  /** Submits presence update with current selection position to ShareDB. */
  private submitPresenceUpdate(): void {
    if (
      !this.editorView ||
      this.readOnly ||
      !this.sharedbDoc.data ||
      !this.userId ||
      !this.presenceViewType
    )
      return;

    const selection = this.editorState.selection;

    try {
      const startQdPos = convertToDeltaPosition(this.editorState.doc, selection.from);
      const endQdPos = convertToDeltaPosition(this.editorState.doc, selection.to);

      logInfoWithDebugFlag("debug_prosemirror", "Submitting presence:", {
        pageId: this.pageId,
        path: this.path,
        pmFrom: selection.from,
        pmTo: selection.to,
        qdStart: startQdPos,
        qdEnd: endQdPos,
        empty: selection.empty,
      });

      // Submit presence to ShareDB
      this.sharedbDoc.localPresence.submit({
        p: this.path,
        t: "rich-text",
        v: this.presenceViewType,
        u: String(this.userId),
        s: {
          u: String(this.userId),
          s: [[startQdPos, endQdPos]],
        },
      });
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      rollbarAndLogError(
        "[ProseMirrorEditor] Error submitting presence",
        {
          pageId: this.pageId,
          path: this.path,
          userId: this.userId,
          pmFrom: selection.from,
          pmTo: selection.to,
        },
        error,
      );
    }
  }

  /** Converts Quill Delta ops to ProseMirror steps and applies them. */
  private handleOp(
    ops: RichTextOpType[],
    source: InstanceType<typeof ProseMirrorEditorInstance>,
  ): void {
    // Ignore our own submissions to prevent feedback loops
    if (source === this) return;

    // Check if any op targets our path
    if (!ops.some((op) => isEqual(op.p?.slice(0, this.path.length), this.path))) return;

    if (!this.editorView) return;

    logInfoWithDebugFlag("debug_prosemirror", "Processing remote ops:", {
      pageId: this.pageId,
      path: this.path,
      ops,
    });

    // Start with current state and apply each op as a transaction
    let newState = this.editorState;
    let docUpdated = false;
    const processedUserIds = new Set<string>();
    const authoredTransactions: { transaction: Transaction; authorUserId?: string }[] = [];

    // Apply each op's steps sequentially using transactions
    for (const op of ops) {
      // Check if this op targets our exact path
      if (isEqual(op.p, this.path)) {
        // Track user ID for pending remote selections updates
        if (op.u) processedUserIds.add(op.u);

        // Convert Quill Delta ops to ProseMirror steps
        try {
          const deltaTransform = convertDeltaToProseMirrorSteps(
            new Transform(newState.doc),
            new Delta(op.o),
          );

          // Create a transaction from current state and apply all steps
          const pendingTransaction = newState.tr;
          for (const step of deltaTransform.steps) {
            pendingTransaction.step(step);
          }

          // Restore local stored marks after steps
          if (this.editorState.storedMarks) {
            pendingTransaction.setStoredMarks(this.editorState.storedMarks);
          }

          // Prevent remote ops from being added to the undo stack so that the user can't undo them
          pendingTransaction.setMeta("addToHistory", false);

          // Apply the transaction to get new state
          newState = newState.apply(pendingTransaction);
          authoredTransactions.push({ transaction: pendingTransaction, authorUserId: op.u });
          docUpdated = true;
        } catch (e: unknown) {
          const error = e instanceof Error ? e : new Error(String(e));
          rollbarAndLogError(
            "[ProseMirrorEditor] Error updating editor state from remote ops",
            { pageId: this.pageId, path: this.path, op },
            error,
          );
          // Resync editor state to ShareDB's canonical state to prevent cascade.
          // sharedbDoc.data already has this op applied; the editor needs to catch up.
          const data = get(this.sharedbDoc.data, this.path) as Delta;
          this.syncContentsFromDoc(data, this.path);
          return;
        }
      }
    }

    if (!docUpdated) return;

    // Update state and view directly (bypassing handleTransaction to avoid feedback loop)
    this.editorState = newState;
    this.editorView.updateState(this.editorState);

    // Keep remote cursors stable while remote doc changes settle
    this.remapRemoteSelections(authoredTransactions);

    // Check for pending selection updates for users whose ops we just processed
    // If presence arrived before the op, we can now process it immediately with updated document
    for (const userId of processedUserIds) {
      const pending = this.remoteSelectionsTimeouts.get(userId);
      if (pending) {
        clearTimeout(pending.timeout);
        this.remoteSelectionsTimeouts.delete(userId);
        pending.updateCallback();
      }
    }
  }

  /** Renders remote cursors and selections by dispatching a transaction. */
  private renderRemoteSelections(): void {
    if (!this.editorView || !this.editorState) return;

    // Convert Map to array for plugin state
    const selectionsArray = Array.from(this.remoteSelectionsData.values());

    // Dispatch a transaction with metadata to update the plugin state
    const tr = this.editorState.tr;
    tr.setMeta(UPDATE_REMOTE_SELECTIONS_META, selectionsArray);
    this.editorView.dispatch(tr);
  }

  /** Converts Quill Delta positions to ProseMirror positions and manages remote cursors. */
  private handlePresence(srcPresenceId: string, presence: PresenceDataType): void {
    if (!this.editorView || !this.editorState) return;

    // We don't show presence on the projector view, because the scaling
    // interferes with the cursor placement, making it look buggy.
    if (isProjectorView()) return;

    // If no presence data, remove the cursor if it exists
    if (!presence) {
      if (this.presenceIdMapping[srcPresenceId]) {
        const userId = this.presenceIdMapping[srcPresenceId];
        this.remoteSelectionsData.delete(srcPresenceId);
        delete this.presenceIdMapping[srcPresenceId];
        this.renderRemoteSelections();
        logInfoWithDebugFlag("debug_prosemirror", "Removed cursor for disconnected user:", {
          pageId: this.pageId,
          path: this.path,
          userId,
        });
      }
      return;
    }

    const presenceUserId = presence.u;

    // Remove cursor if user is active in another field
    if (!isEqual(presence.p, this.path)) {
      if (this.presenceIdMapping[srcPresenceId]) {
        const userId = this.presenceIdMapping[srcPresenceId];
        this.remoteSelectionsData.delete(srcPresenceId);
        this.renderRemoteSelections();
        logInfoWithDebugFlag("debug_prosemirror", "Removed cursor for user in different field:", {
          pageId: this.pageId,
          path: this.path,
          userId,
        });
      }
      return;
    }

    // Filter presence based on view type
    // We only display presence coming from the same view as the current tab,
    // with the following exceptions:
    // - On the class responses tab, you can see presence from everyone
    // - Instructors editing the template can show presence on the student draft too
    if (this.presenceViewType) {
      const isClassResponses = this.presenceViewType === "c";
      const matchingPresenceView = presence.v === this.presenceViewType;
      const comesFromTemplate = presence.v === "t";
      const isDraftPage = this.presenceViewType === "d";
      if (!matchingPresenceView && !isClassResponses && !(comesFromTemplate && isDraftPage)) {
        return;
      }
    }

    // Validate presence type and structure
    if (presence.t !== "rich-text" || !presence.s) {
      rollbarAndLogError("[ProseMirrorEditor] Invalid remote presence data", {
        pageId: this.pageId,
        path: this.path,
        presence,
      });
      return;
    }

    // Update ID mapping
    this.presenceIdMapping[srcPresenceId] = presenceUserId;

    // Ignore our own presence or empty selections
    if (presenceUserId === this.userId || !presence.s.s || presence.s.s.length === 0) {
      return;
    }

    // Get the first selection
    // TODO: Support multiple selections
    const sel = presence.s.s[0];
    const len = Math.abs(sel[1] - sel[0]);
    const min = Math.min(sel[0], sel[1]);

    const startQdPos = min;
    const endQdPos = min + len;

    const username = getUserShortName(this.users[presenceUserId]);
    const color = getUserColor(presenceUserId);
    const existingData = this.remoteSelectionsData.get(srcPresenceId);
    const isFirstRender = !existingData;

    logInfoWithDebugFlag("debug_prosemirror", "Processing remote presence:", {
      pageId: this.pageId,
      path: this.path,
      srcPresenceId,
      presenceUserId,
      username,
      qdStart: startQdPos,
      qdEnd: endQdPos,
      presenceViewType: this.presenceViewType,
    });

    const updateCallback = () => {
      try {
        const startPmPos = convertToProseMirrorPosition(this.editorState.doc, startQdPos);
        const endPmPos = convertToProseMirrorPosition(this.editorState.doc, endQdPos);

        const selectionData: RemoteSelectionData = {
          srcPresenceId,
          userId: presenceUserId,
          username,
          from: startPmPos,
          to: endPmPos,
          color,
          isFirstRender,
        };

        this.remoteSelectionsData.set(srcPresenceId, selectionData);
        this.renderRemoteSelections();
      } catch (e) {
        const error = e instanceof Error ? e : new Error(String(e));
        rollbarAndLogError(
          "[ProseMirrorEditor] Error processing remote presence",
          {
            pageId: this.pageId,
            path: this.path,
            srcPresenceId,
            presenceUserId,
            startQdPos,
            endQdPos,
          },
          error,
        );
      }
    };

    // If a previous pending update exists for this user, cancel its timeout
    const pending = this.remoteSelectionsTimeouts.get(presenceUserId);
    if (pending) {
      clearTimeout(pending.timeout);
    }

    // Delay rendering selections to handle race condition where presence arrives before ops.
    //
    // ShareDB limitation: Presence updates and document ops are sent separately
    // and there's no way to correlate them. When a user types or deletes, their presence
    // (cursor position) and the op (text insertion/deletion) may arrive in either order.
    //
    // Problem: If presence arrives first, we calculate cursor positions using stale document
    // state, causing the cursor to briefly appear on the wrong line before the op arrives
    // and corrects the document. This causes a visible flash as the cursor jumps.
    //
    // Solution: Store updateCallback keyed by userId. If an op arrives for that user, we execute
    // the updateCallback immediately with the updated document. Otherwise, the timeout fires as
    // a fallback for presence updates not associated with an op.
    const timeout = setTimeout(() => {
      this.remoteSelectionsTimeouts.delete(presenceUserId);
      updateCallback();
    }, PRESENCE_RENDER_DELAY_MS);

    this.remoteSelectionsTimeouts.set(presenceUserId, { timeout, updateCallback });
  }
}
